import React, { useEffect, useRef } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Plus } from "lucide-react";
import type { FluxoBlockType } from "@/hooks/useFluxos";
import { BLOCK_META, BLOCK_TYPES } from "./blockMeta";
import { useTranslation } from "react-i18next";

interface AddBlockMenuProps {
  onAdd: (type: FluxoBlockType) => void;
}

interface PaletteItemProps {
  type: FluxoBlockType;
  onAdd: (type: FluxoBlockType) => void;
}

const PaletteItem: React.FC<PaletteItemProps> = ({ type, onAdd }) => {
  const { t } = useTranslation("automations");
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${type}`,
    data: { fromPalette: true, blockType: type },
  });

  // Após um drag real, o navegador ainda dispara click no botão; evita inserir duas vezes
  const wasDragged = useRef(false);
  useEffect(() => {
    if (isDragging) wasDragged.current = true;
  }, [isDragging]);

  const handleClick = () => {
    if (wasDragged.current) {
      wasDragged.current = false;
      return;
    }
    onAdd(type);
  };

  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className={`flex items-center gap-2 px-3 min-h-[38px] xl:w-full rounded-[10px] border border-border bg-white text-[#0F172A] text-sm hover:bg-[#F1F5F9] transition-colors cursor-grab active:cursor-grabbing touch-none dark:rounded-[8px] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-neutral-50 dark:hover:bg-[#1f1f23] ${
        isDragging ? "opacity-40" : ""
      }`}
    >
      <Plus size={14} className="text-[#94A3B8] dark:text-neutral-400" />
      {BLOCK_META[type].icon}
      {t(BLOCK_META[type].labelKey)}
    </button>
  );
};

const AddBlockMenu: React.FC<AddBlockMenuProps> = ({ onAdd }) => {
  return (
    <div className="flex flex-wrap xl:flex-col gap-2">
      {BLOCK_TYPES.map((type) => (
        <PaletteItem key={type} type={type} onAdd={onAdd} />
      ))}
    </div>
  );
};

export default AddBlockMenu;
